<template>
  <BaseFilterButtons
    class="my-1"
    :filter-groups="filterGroups"
    :filter-values="filterValues"
    @filter-click="handleFilterClick"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { KitchenStatusFilter, OrderTypeFilter } from '@/composables/useKitchenFilters';
import BaseFilterButtons from '@/components/common/BaseFilterButtons.vue';

interface StatusTab {
  value: KitchenStatusFilter;
  label: string;
  count: number;
}

interface OrderTypeTab {
  value: OrderTypeFilter;
  label: string;
  count: number;
}

interface Props {
  selectedStatus: KitchenStatusFilter;
  selectedOrderType: OrderTypeFilter;
  statusTabs: StatusTab[];
  orderTypeTabs: OrderTypeTab[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:selectedStatus': [value: KitchenStatusFilter];
  'update:selectedOrderType': [value: OrderTypeFilter];
}>();

const { t } = useI18n();

// Status options converted to filter group format
const statusOptions = computed(() => 
  props.statusTabs.map(tab => ({
    value: tab.value,
    label: tab.label
  }))
);

// Order type options converted to filter group format
const orderTypeOptions = computed(() => 
  props.orderTypeTabs.map(tab => ({
    value: tab.value,
    label: tab.label
  }))
);

// Filter groups for BaseFilterButtons
const filterGroups = computed(() => [
  {
    name: 'status',
    options: statusOptions.value,
    activeClass: 'bg-indigo-600 text-white',
    allowToggle: false // Status no permite toggle en cocina
  },
  {
    name: 'orderType',
    options: orderTypeOptions.value,
    activeClass: 'bg-green-600 text-white',
    allowToggle: true
  }
]);

// Current filter values
const filterValues = computed(() => ({
  status: props.selectedStatus,
  orderType: props.selectedOrderType
}));

// Event handlers
const handleFilterClick = (groupName: string, value: string | number | undefined) => {
  switch (groupName) {
    case 'status':
      emit('update:selectedStatus', (value || 'all') as KitchenStatusFilter);
      break;
    case 'orderType':
      emit('update:selectedOrderType', value as OrderTypeFilter);
      break;
  }
};
</script>

<style scoped>
/* Smooth scrolling for tabs on mobile */
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
}

.overflow-x-auto::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}
</style>
